import React, { useEffect, useState } from 'react';

import { useMediaQuery } from 'react-responsive';
import 'react-phone-input-2/lib/style.css';

import Text from 'common/components/Text/Text';
import { SessionState, deleteStoreAttachment } from 'features/session/sessionSlice';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { CommonHash } from 'app/types';
import styles from './UserInformation.module.scss';
import {
  useDeleteAttachmentMutation,
  useDeleteBackofficeAttachmentMutation,
} from 'services/exploreApi';
import { useLocation } from 'react-router-dom';

interface CompProps {
  setAnswer?: (h: CommonHash) => void;
  fileType: string;
  answers: CommonHash;
  setInputOptionOfForm?: (key: string, value: any) => void;
  index?: number;
}

const UserFileUpload = ({
  setAnswer,
  fileType,
  answers,
  setInputOptionOfForm,
  index,
}: CompProps): React.ReactElement => {
  const location = useLocation();
  let deleteAttachment: any;
  const { user } = useAppSelector(({ session }: { session: SessionState }) => session);
  if (location.pathname.includes('backoffice')) {
    [deleteAttachment] = useDeleteBackofficeAttachmentMutation();
  } else {
    [deleteAttachment] = useDeleteAttachmentMutation();
  }
  const [selectedFile, setSelectedFile] = useState<File>();
  const [hasFile, setHasFile] = useState<boolean>(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const fileRef = React.createRef<HTMLInputElement>();
  const dispatch = useAppDispatch();
  const userFile = () => {
    switch (fileType) {
      case 'passport':
        return user?.passport;
      case 'cv':
        return user?.documents.find((document) => document.name == 'CV')?.url;
      case 'transcripts':
        return answers && answers['transcripts'];
      case 'degree_certificate':
        return answers && answers['degree_certificate'];
      case 'english_exam':
        return answers && answers['english_exam'];
      case 'technical_exam':
        return answers && answers['technical_exam'];
    }
  };

  const prettierFileTypeName = () => {
    switch (fileType) {
      case 'passport':
        return 'Passport';
      case 'cv':
        return 'CV';
      case 'transcripts':
        return 'Transcripts';
      case 'degree_certificate':
        return 'Degree Certificate';
      case 'english_exam':
        return 'English Test';
      case 'technical_exam':
        return 'Technical Test';
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    setSelectedFile(file);
  };

  const showOpenFileDialog = () => {
    fileRef?.current?.click();
  };

  const setInputOption = (key: string, value: any) => {
    if (setAnswer) {
      setAnswer((a: any) => ({ ...a, [key]: value }));
    } else if (setInputOptionOfForm) {
      setInputOptionOfForm(key, value);
    }
  };

  const handleRemoveFile = () => {
    setHasFile(false);
    setInputOption(`${fileType}`, null);
    if (location.pathname.includes('backoffice')) {
      deleteAttachment({ file_type: fileType, index: index, id: user?.id });
    } else {
      deleteAttachment({ file_type: fileType, index: index });
    }
    dispatch(deleteStoreAttachment({ fileType: fileType, index: index }));
  };

  useEffect(() => {
    selectedFile && setInputOption(fileType, selectedFile);
  }, [selectedFile]);

  useEffect(() => {
    userFile() && setHasFile(true);
    answers && answers[fileType] && setSelectedFile(answers[fileType]);
  }, []);
  return (
    <div className={styles.inputsContainter}>
      <div className={styles.inputGroup} style={{ width: '100%' }}>
        <Text
          size={16}
          weight={isMobile ? 500 : 600}
          inlineStyle={{ marginBottom: '12px' }}
          color="primary"
        >
          {prettierFileTypeName()} upload
        </Text>
        {hasFile ? (
          <div className={styles.uploadedFileBox}>
            <div className={styles.leftText}>
              <Text size={16} weight={500} color="secondary">
                Uploaded
              </Text>
              <u>{`${user?.name} ${prettierFileTypeName()}`}</u>
            </div>
            <div className={styles.iconsContainer}>
              <div className={styles.downloadIcon} onClick={() => window.open(userFile())}>
                <i className="bi bi-download"></i>
              </div>
              <div className={styles.cancelIcon} onClick={handleRemoveFile}>
                <i className="bi bi-x-lg"></i>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.fileBox}>
            {isMobile ? (
              <Text size={16} weight={600} color="secondary" inline>
                <input
                  ref={fileRef}
                  type="file"
                  style={{ display: 'none' }}
                  accept=".pdf, .jpg, .png"
                  onChange={handleChange}
                />
                <div className={styles.browseLink} onClick={showOpenFileDialog}>
                  Browse{' '}
                </div>
                our file here
              </Text>
            ) : (
              <Text size={16} weight={600} color="secondary" inline>
                <input
                  ref={fileRef}
                  type="file"
                  style={{ display: 'none' }}
                  accept=".pdf, .jpg, .png"
                  onChange={handleChange}
                />
                Drop your file here or
                <div className={styles.browseLink} onClick={showOpenFileDialog}>
                  {' '}
                  browse
                </div>
              </Text>
            )}
            {selectedFile && (
              <Text
                size={isMobile ? 12 : 14}
                color="secondary"
                inlineStyle={{ marginTop: '10px', marginBottom: '10px' }}
              >
                {selectedFile.name}
              </Text>
            )}
            <div className={styles.bottomText}>
              {isMobile && (
                <Text size={12} weight={500} color="secondary" inlineStyle={{ lineHeight: '24px' }}>
                  Suggest File Size: 3MB
                </Text>
              )}
              <Text
                size={isMobile ? 12 : 14}
                weight={500}
                color="secondary"
                inlineStyle={{ lineHeight: '24px' }}
              >
                {isMobile
                  ? 'Acceptable formats: .PDF, JPG or PNG'
                  : 'Max. File Size: 20MB / Acceptable formats: .PDF, JPG or PNG'}
              </Text>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default UserFileUpload;
